import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PickList, Modal, Checkbox } from "@velocity-ehs/component-library";
import { differenceWith, isEqual, map, uniqWith } from "lodash";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import classes from "./LocationsManageApplications.module.scss";
import { PickListChangeEvent } from "primereact/picklist";
import { ApplicationBundlesActions } from "../../../hooks/useApplicationBundlesMutation";

interface ApplicationWithBundle {
  id: string;
  isAssigned?: boolean;
  bundle: ApplicationBundle;
}

export interface ApplicationBundle {
  id: string;
  name: string;
  applyToDescendants: boolean;
}

interface Props {
  isLoading?: boolean;
  locationApplications?: ApplicationWithBundle[];
  customerApplications?: ApplicationWithBundle[];
  mutate?: (
    action: ApplicationBundlesActions,
    locationBundles: ApplicationBundle[],
    locationId: String
  ) => Promise<any>;
  showModal?: boolean;
  closeModal?: () => void;
  readOnly?: boolean;
  refetchLocationBundles?: () => any;
}

const LocationsManageApplications: React.FC<Props> = ({
  isLoading,
  locationApplications,
  customerApplications,
  mutate,
  showModal,
  closeModal,
  readOnly,
  refetchLocationBundles,
}) => {
  const { control, reset } = useForm({
    mode: "onBlur",
  });
  const [locationBundles, setLocationBundles] = useState<ApplicationBundle[]>(
    []
  );
  const [customerBundles, setCustomerBundles] = useState<ApplicationBundle[]>(
    []
  );

  const [shouldDedupe, setShouldDedupe] = useState<boolean>(true);

  const { t } = useTranslation();

  const { id } = useParams() as { id: string };

  const handleSubmit = (event: any) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (locationBundles.length === 0 && customerBundles.length === 0) {
      setLocationBundles(getApplicationBundles(locationApplications, false));
      setCustomerBundles(
        removeAlreadyAssignedBundlesfromCustomer(
          getApplicationBundles(customerApplications, true),
          locationBundles,
          setShouldDedupe
        )
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoading]);

  useEffect(() => {
    /* 
    need the dedupe function to run at least once when locationBundles is not empty to be sure it's not just deduping before locationBundles is populated
    */
    if (shouldDedupe) {
      setCustomerBundles(
        removeAlreadyAssignedBundlesfromCustomer(
          getApplicationBundles(customerApplications, true),
          locationBundles,
          setShouldDedupe
        )
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [locationBundles.length]);

  const onSubmit = () => {
    mutate(ApplicationBundlesActions.CREATE, locationBundles, id).then(() => {
      refetchLocationBundles();
    });

    closeModal();
    reset({ keepValues: false });
  };

  const handleCheckboxClick = (event: any) => {
    event.stopPropagation();
    const updatedBundles = map(locationBundles, (bundle) => {
      return bundle.name === event.target.id
        ? {
            name: bundle.name,
            id: bundle.id,
            applyToDescendants: event.target.checked,
          }
        : { ...bundle };
    });
    setLocationBundles(updatedBundles);
  };

  const targetTemplate = (item: any, titleKey: string) => {
    return (
      <div className={classes.targetTemplate}>
        <h3>{item[titleKey]}</h3>
        <Checkbox
          control={control}
          name={item[titleKey]}
          onClick={handleCheckboxClick}
          id={item[titleKey]}
        />
      </div>
    );
  };

  const targetFilterTemplate = () => {
    return t("location_application_assignment_target_footer");
  };

  const onPickListChange = (event: PickListChangeEvent) => {
    setLocationBundles(event.target);
    setCustomerBundles(event.source);
  };

  return (
    <>
      <div data-testid="location-manage-applications">
        <h2>{t("locations_applications")}</h2>
        <form id="locationsNewForm" autoComplete="off" onSubmit={handleSubmit}>
          <PickList
            sourceData={customerBundles}
            targetData={locationBundles}
            onChange={onPickListChange}
            isLoading={isLoading}
            titleKey="name"
            descriptionKey="description"
            sourceTitle={t("universal_available")}
            sourceSubtitle={t("locations_applications_card_source_description")}
            targetTitle={t("universal_selected")}
            targetSubtitle={t("locations_applications_card_target_description")}
            targetTemplate={targetTemplate}
            subtitleHeaderFirstColumnTitle={t("locations_applications")}
            subtitleHeaderSecondColumnTitle={t(
              "location_application_assignment_second_column_title"
            )}
            disabled={readOnly}
            targetFilterTemplate={targetFilterTemplate}
          />
        </form>
      </div>
      <Modal
        title={t("location_application_assignment_modal_title")}
        show={showModal}
        onClose={closeModal}
        footerButtons={[
          {
            label: t("universal_yes"),
            onClick: onSubmit,
            loading: isLoading,
            id: "deleteHierarchyModal",
          },
          {
            label: t("universal_no"),
            className: "p-button-text",
            onClick: closeModal,
            id: "cancelDeleteHierarchyModal",
          },
        ]}
      >
        {t("location_application_assignment_modal_body")}
      </Modal>
    </>
  );
};

export default LocationsManageApplications;

function getApplicationBundles(
  applicationArray: ApplicationWithBundle[],
  isCustomerBundle: boolean
) {
  let bundles = [];
  applicationArray?.forEach((application) => {
    if (application.isAssigned || !isCustomerBundle) {
      bundles.push({
        name: application.bundle.name,
        id: application.bundle.id,
        applyToDescendants: false,
      });
    }
  });
  return uniqWith(bundles, isEqual);
}

function removeAlreadyAssignedBundlesfromCustomer(
  customerBundles: ApplicationBundle[],
  locationBundles: ApplicationBundle[],
  setShouldDedupe: any
) {
  if (locationBundles?.length > 1) {
    setShouldDedupe(false);
  }
  return differenceWith(customerBundles, locationBundles, isEqual);
}
